import Vue from 'vue';
import { queryClient } from '@src/queries/client';

// Whether the core API is reachable, judged from the panel's own requests. One
// failed request is never enough: the first network/gateway failure starts
// probing the API root, and an outage is reported only once it has stayed
// unreachable for `confirmAfter`, so a restart or a dropped request goes unnoticed.
export const API_STATUS = { UP: 'up', CHECKING: 'checking', DOWN: 'down', RESTORED: 'restored' };

export const TIMINGS = {
    firstProbe: 1000,
    checkEvery: 3000,
    confirmAfter: 10000,
    downPoll: 5000,
    downPollMax: 20000,
    restoredFor: 6000,
    probeTimeout: 5000,
    toastBurst: 1000,
    relapseWithin: 60000,
};

const GATEWAY_STATUSES = [502, 503, 504];

const requestUrl = (config = {}) => {
    const url = config.url || '';
    return /^https?:\/\//i.test(url) ? url : `${config.baseURL || ''}${url}`;
};

// No answer at all, or one from the proxy in front of a dead API. Cancels and
// aborts (navigating away) don't count.
const isUnreachable = (error) => {
    if (error.response) return GATEWAY_STATUSES.includes(error.response.status);
    if (error.code === 'ERR_NETWORK' || error.code === 'ETIMEDOUT') return true;
    return error.code === 'ECONNABORTED' && /timeout/i.test(error.message || '');
};

export function createApiStatusMonitor({
    apiBase,
    probe,
    onRecovered = () => {},
    isOffline = () => typeof navigator !== 'undefined' && navigator.onLine === false,
}) {
    const state = Vue.observable({ status: API_STATUS.UP });
    let timer = null;
    let failingSince = 0;
    let downPoll = TIMINGS.downPoll;
    let lastErrorToastAt = -Infinity;
    let restoredAt = -Infinity;
    // Bumped whenever the API answers, so a probe started before that is ignored.
    let generation = 0;

    const isCoreRequest = (config) => !!apiBase && requestUrl(config).startsWith(apiBase);

    const schedule = (fn, ms) => {
        clearTimeout(timer);
        timer = setTimeout(fn, ms);
    };

    const markReachable = () => {
        if (state.status === API_STATUS.UP || state.status === API_STATUS.RESTORED) return;
        generation += 1;
        clearTimeout(timer);
        if (state.status === API_STATUS.DOWN) {
            state.status = API_STATUS.RESTORED;
            restoredAt = Date.now();
            schedule(() => { state.status = API_STATUS.UP; }, TIMINGS.restoredFor);
        } else {
            state.status = API_STATUS.UP;
        }
        onRecovered();
    };

    const runProbe = async () => {
        const startedIn = generation;
        const reachable = await probe().catch(() => false);
        if (startedIn !== generation) return;
        if (reachable) return markReachable();

        if (state.status === API_STATUS.CHECKING && Date.now() - failingSince >= TIMINGS.confirmAfter) {
            state.status = API_STATUS.DOWN;
            downPoll = TIMINGS.downPoll;
        } else if (state.status === API_STATUS.DOWN) {
            downPoll = Math.min(downPoll * 1.5, TIMINGS.downPollMax);
        }
        schedule(runProbe, state.status === API_STATUS.DOWN ? downPoll : TIMINGS.checkEvery);
    };

    // The offline notice already covers a browser with no connection. An API
    // that fails again soon after an outage (a crash loop) is still down, so
    // the banner returns without a new confirmation; the quick probe takes it
    // back off if this was a stray late failure.
    const markUnreachable = () => {
        if (state.status === API_STATUS.CHECKING || state.status === API_STATUS.DOWN) return;
        if (isOffline()) return;
        if (Date.now() - restoredAt < TIMINGS.relapseWithin) {
            state.status = API_STATUS.DOWN;
            downPoll = TIMINGS.downPoll;
            schedule(runProbe, TIMINGS.firstProbe);
            return;
        }
        state.status = API_STATUS.CHECKING;
        failingSince = Date.now();
        lastErrorToastAt = -Infinity;
        schedule(runProbe, TIMINGS.firstProbe);
    };

    return {
        state,

        reportResponse(response) {
            if (isCoreRequest(response?.config)) markReachable();
        },

        reportError(error) {
            if (!error || !isCoreRequest(error.config)) return;
            if (isUnreachable(error)) markUnreachable();
            else if (error.response) markReachable();
        },

        // While checking, a burst of failures (a page's parallel requests)
        // shows one toast, but a later one, like a retried save, still shows
        // its own: a blip ends without a banner. None while the banner is up.
        allowErrorToast() {
            if (state.status === API_STATUS.DOWN) return false;
            if (state.status !== API_STATUS.CHECKING) return true;
            if (Date.now() - lastErrorToastAt < TIMINGS.toastBurst) return false;
            lastErrorToastAt = Date.now();
            return true;
        },
    };
}

const API_BASE = process.env.VUE_APP_API || '';

// Any non-5xx answer from the root means the API process is serving.
const probeApiRoot = async () => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), TIMINGS.probeTimeout);
    try {
        const res = await fetch(`${API_BASE.replace(/\/+$/, '')}/`, { cache: 'no-store', signal: controller.signal });
        return res.status < 500;
    } finally {
        clearTimeout(timer);
    }
};

export const apiStatus = createApiStatusMonitor({
    apiBase: API_BASE,
    probe: probeApiRoot,
    // Screens whose queries failed while the API was unreachable refetch on their own.
    onRecovered: () => queryClient.refetchQueries({ type: 'active', predicate: (query) => query.state.status === 'error' }),
});
